import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as PIXI from "pixi.js";
import { ASSETS } from "../../assets";
import { initializeStyles } from "../../utils/injectStyles";

export const loadAssets = createAsyncThunk(
  "assets/loadAssets",
  async (_, { rejectWithValue }) => {
    try {
      // Inject CSS styles and preload fonts
      await initializeStyles();

      // Wait for all web fonts to load before proceeding
      // This ensures PixiJS Text components can use custom fonts immediately
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      } else {
        // Fallback: wait a bit for fonts to start loading
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // 🚀 PARALLEL LOADING - Load all textures simultaneously
      const texturePromises = ASSETS.textures.map(async (asset) => {
        try {
          // Add asset with alias so it can be retrieved by name
          PIXI.Assets.add({ alias: asset.name, src: asset.url });

          if (!PIXI.Assets.cache.has(asset.name)) {
            await PIXI.Assets.load(asset.name);
          }
        } catch (textureError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all spine animations simultaneously
      const spinePromises = ASSETS.spineAnimations.map(async (animation) => {
        const baseName = animation.name;
        const jsonPath = animation.json;
        const atlasPath = jsonPath.replace(".json", ".atlas");

        // Validate paths before attempting to load
        if (!jsonPath || !atlasPath || !baseName) {
          throw new Error(
            `Invalid spine configuration for ${baseName}: missing path or name`
          );
        }

        // Check for malformed paths (should not contain undefined, null, HTML, or empty segments)
        const invalidPatterns = [
          "undefined",
          "null",
          "<!DOCTYPE",
          "<html",
          "DOCTYPE html",
          "window.__OPTIONS__",
          "response.ok",
          "Call the session",
        ];

        const hasInvalidPattern = invalidPatterns.some(
          (pattern) =>
            jsonPath.includes(pattern) ||
            atlasPath.includes(pattern) ||
            baseName.includes(pattern)
        );

        if (
          hasInvalidPattern ||
          jsonPath.endsWith("/.json") ||
          atlasPath.endsWith("/.atlas") ||
          !jsonPath.endsWith(".json") ||
          !atlasPath.endsWith(".atlas")
        ) {
          throw new Error(`Invalid spine path for ${baseName}: ${jsonPath}`);
        }

        // Final validation: ensure paths are valid URLs and don't contain HTML
        try {
          // Validate URL format
          const testUrl = new URL(jsonPath, window.location.origin);
          if (!testUrl.pathname.endsWith(".json")) {
            throw new Error(`Invalid JSON path format: ${jsonPath}`);
          }
        } catch (urlError) {
          // If URL construction fails, it's likely a relative path - validate manually
          if (
            !jsonPath.startsWith("/") &&
            !jsonPath.startsWith("./") &&
            !jsonPath.startsWith("http")
          ) {
            throw new Error(`Invalid path format: ${jsonPath}`);
          }
        }

        try {
          // Skip if already loaded (e.g. logo spine pre-loaded by backport Loader)
          if (!PIXI.Assets.cache.has(`${baseName}-skeleton`)) {
            PIXI.Assets.add({ alias: `${baseName}-skeleton`, src: jsonPath });
            PIXI.Assets.add({ alias: `${baseName}-atlas`, src: atlasPath });
            await PIXI.Assets.load([
              `${baseName}-skeleton`,
              `${baseName}-atlas`,
            ]);
          }
        } catch (spineError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all bitmap fonts simultaneously
      const fontPromises = ASSETS.bitmapFonts.map(async (font) => {
        try {
          if (!PIXI.Assets.cache.has(font.json)) {
            await PIXI.Assets.load(font.json);
          }
        } catch (fontError) {
          // Missing asset won't block other assets - fail silently
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // Wait for ALL assets to finish loading (happens in parallel!)
      // Note: Failed assets are handled gracefully - game continues without them
      // Failed assets return null, but we don't need to count them for production
      await Promise.all([
        ...texturePromises,
        ...spinePromises,
        ...fontPromises,
      ]);

      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assetsSlice = createSlice({
  name: "assets",
  initialState: {
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAssets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadAssets.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(loadAssets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "An unknown error occurred.";
      });
  },
});

export default assetsSlice.reducer;
